import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useEntitlements,
    useCreateEntitlement,
    useUpdateEntitlement,
    useDeleteEntitlement,
} from '../../hooks/entitlements';
import type { Entitlement, EntitlementStatus, CreateEntitlementDto } from '../../types/types';
import { t } from '../../i18n/t';

const statusLabel = (s: string) => {
    const key = `entitlements.status.${s}` as const;
    const label = t(key);
    return label === key ? s : label;
};

const DashboardEntitlements = () => {
    const { data: entitlements = [], isLoading, refetch } = useEntitlements();
    const deleteMutation = useDeleteEntitlement();
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<Entitlement | null>(null);

    const handleDelete = async (entitlement: Entitlement) => {
        if (!window.confirm(t('entitlements.deleteConfirm', { type: entitlement.type }))) return;
        try {
            await deleteMutation.mutateAsync(entitlement.id);
        } catch (e) {
            console.error(e);
            alert(t('entitlements.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('entitlements.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('entitlements.new')}
                </Button>
                <Tooltip title={t('entitlements.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>{t('entitlements.colType')}</TableCell>
                            <TableCell>{t('entitlements.colDescription')}</TableCell>
                            <TableCell>{t('entitlements.colStatus')}</TableCell>
                            <TableCell align="right">{t('entitlements.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entitlements.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {t('entitlements.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            entitlements.map((entitlement) => (
                                <TableRow key={entitlement.id}>
                                    <TableCell>{entitlement.id}</TableCell>
                                    <TableCell>{entitlement.type}</TableCell>
                                    <TableCell>{entitlement.description}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel(entitlement.status)}
                                            color={entitlement.status === 'active' ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setEditing(entitlement)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(entitlement)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <EntitlementFormDialog
                    title={t('entitlements.dialogNew')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
            {editing && (
                <EntitlementFormDialog
                    title={t('entitlements.dialogEdit')}
                    entitlement={editing}
                    onClose={() => setEditing(null)}
                />
            )}
        </Box>
    );
};

interface EntitlementFormDialogProps {
    title: string;
    entitlement?: Entitlement;
    onClose: () => void;
}

const EntitlementFormDialog: React.FC<EntitlementFormDialogProps> = ({
    title,
    entitlement,
    onClose,
}) => {
    const createMutation = useCreateEntitlement();
    const updateMutation = useUpdateEntitlement();
    const isEdit = entitlement != null;

    const [type, setType] = useState(entitlement?.type ?? '');
    const [description, setDescription] = useState(entitlement?.description ?? '');
    const [status, setStatus] = useState<EntitlementStatus>(entitlement?.status ?? 'active');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!type.trim()) {
            alert(t('entitlements.enterType'));
            return;
        }

        const dto: CreateEntitlementDto = {
            type: type.trim(),
            description: description.trim(),
            status,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await updateMutation.mutateAsync({ id: entitlement.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('entitlements.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('entitlements.fieldType')}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={isEdit}
                    />
                    <TextField
                        label={t('entitlements.fieldDescription')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                        size="small"
                        fullWidth
                    />
                    <RadioGroup
                        row
                        value={status}
                        onChange={(e) => setStatus(e.target.value as EntitlementStatus)}
                    >
                        <FormControlLabel
                            value="active"
                            control={<Radio />}
                            label={t('entitlements.status.active')}
                        />
                        <FormControlLabel
                            value="inactive"
                            control={<Radio />}
                            label={t('entitlements.status.inactive')}
                        />
                        <FormControlLabel
                            value="archived"
                            control={<Radio />}
                            label={t('entitlements.status.archived')}
                        />
                    </RadioGroup>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardEntitlements;
